import { t } from '@lingui/core/macro'
import { notification } from 'antd'
import { ArgsProps, NotificationInstance } from 'antd/es/notification/interface'
import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react'

export type NotificationProps = Partial<
  Omit<ArgsProps, 'message' | 'description'>
>

export type NotificationFunction = (
  message: string,
  args?: NotificationProps
) => void

export type GeckoNotificationContextType = {
  notificationApi: NotificationInstance
  successNotification: NotificationFunction
  errorNotification: NotificationFunction
  infoNotification: NotificationFunction
  warningNotification: NotificationFunction
}

const DEFAULT_NOTIFICATION_PROPS: NotificationProps = {
  placement: 'topRight',
  duration: 10,
}

const SUCCESS_DURATION_SECONDS = 4

/** Newer toasts push the oldest out instead of stacking without bound. */
const MAX_VISIBLE_NOTIFICATIONS = 3

const GeckoNotificationContext =
  createContext<GeckoNotificationContextType | null>(null)

export const useGeckoNotifications: () => GeckoNotificationContextType = () => {
  const context = useContext(GeckoNotificationContext)
  if (!context || context.notificationApi === null) {
    console.error(
      'Notifications context not configured - please place a GeckoNotificationProvider at the top level of your page.'
    )
    throw new Error(
      'useGeckoNotifications must be used within a GeckoNotificationProvider'
    )
  }
  return context
}

export const GeckoNotificationProvider: FC<{
  children: ReactNode
  notificationProps?: NotificationProps
}> = ({ children, notificationProps }) => {
  const [api, contextHolder] = notification.useNotification({
    maxCount: MAX_VISIBLE_NOTIFICATIONS,
  })

  const props = useMemo(
    () => ({ ...DEFAULT_NOTIFICATION_PROPS, ...notificationProps }),
    [notificationProps]
  )
  const successDuration =
    notificationProps?.duration ?? SUCCESS_DURATION_SECONDS

  const successNotification = useCallback(
    (message: string, overrideProps?: NotificationProps) => {
      api.success({
        title: t`Success`,
        description: message,
        ...props,
        duration: successDuration,
        ...overrideProps,
      })
    },
    [api, props, successDuration]
  )

  const errorNotification = useCallback(
    (message: string, overrideProps?: NotificationProps) => {
      console.error(message)
      api.error({
        title: message,
        ...props,
        ...overrideProps,
      })
    },
    [api, props]
  )

  const infoNotification = useCallback(
    (message: string, overrideProps?: NotificationProps) =>
      api.info({
        title: message,
        ...props,
        ...overrideProps,
      }),
    [api, props]
  )

  const warningNotification = useCallback(
    (message: string, overrideProps?: NotificationProps) =>
      api.warning({
        title: message,
        ...props,
        ...overrideProps,
      }),
    [api, props]
  )

  return (
    <GeckoNotificationContext.Provider
      value={useMemo(
        () => ({
          notificationApi: api,
          successNotification,
          errorNotification,
          infoNotification,
          warningNotification,
        }),
        [
          api,
          successNotification,
          errorNotification,
          infoNotification,
          warningNotification,
        ]
      )}>
      {contextHolder}
      {children}
    </GeckoNotificationContext.Provider>
  )
}
